import { formatDate, formatNumber, useLocale, useTranslation } from '@multaqa/i18n'
import { Label } from '@multaqa/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useExchangeRateQuote } from '@/lib/exchange-rate-quote'
import { meQuery } from '@/lib/me'

export function documentCurrenciesQuery(companyId: string | null) {
  return {
    queryKey: ['companies', companyId, 'accounting', 'document-currencies'] as const,
    enabled: Boolean(companyId),
    queryFn: async () => {
      const response = await api.GET('/accounting/document-currencies', {
        headers: { 'X-Company-Id': companyId ?? '' },
      })
      if (!response.data) throw new Error('GET /accounting/document-currencies returned no body')
      return response.data.data
    },
  }
}

/**
 * The currency a sales or purchase order is raised in (WP10). The server
 * lists the currencies the company has a rate for; an empty value means the
 * company currency. A company with no foreign rate sees no picker at all,
 * because there is nothing to choose.
 *
 * With `rateDate`, the rate shown is the server's quote for that date — the
 * rate the document stores (foreign-currency suppliers, Decision 2) — rather
 * than today's.
 */
export function DocumentCurrencySelect({
  value,
  onChange,
  hidden = false,
  rateDate,
}: {
  value: string
  onChange: (currency: string) => void
  hidden?: boolean
  rateDate?: string
}) {
  const { t } = useTranslation('common')
  const { locale } = useLocale()
  const { data: me } = useQuery(meQuery)
  const companyId = me?.active_company_id ?? null
  const currencies = useQuery(documentCurrenciesQuery(companyId))
  const options = currencies.data ?? []
  const base = options.find((option) => option.is_base)
  const chosen = options.find((option) => option.code === value)
  const foreign = chosen && !chosen.is_base ? chosen.code : null
  const quote = useExchangeRateQuote(companyId, rateDate ? foreign : null, rateDate ?? '')

  if (hidden || options.length < 2 || !base) return null

  const rate = (value: string) => formatNumber(Number(value), locale, { maximumFractionDigits: 6 })

  return (
    <div className="flex max-w-xs flex-col gap-1.5" data-testid="document-currency">
      <Label htmlFor="document-currency-select">{t('documentCurrency.label')}</Label>
      <Select value={value || base.code} onValueChange={onChange}>
        <SelectTrigger id="document-currency-select" data-testid="document-currency-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.code} value={option.code}>
              {option.code}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {foreign && !rateDate && chosen ? (
        <p className="text-muted-foreground text-small" data-testid="document-currency-rate">
          {t('documentCurrency.rate', {
            currency: foreign,
            rate: rate(chosen.rate),
            base: base.code,
          })}
        </p>
      ) : null}
      {foreign && rateDate && quote.data ? (
        <p className="text-muted-foreground text-small" data-testid="document-currency-rate">
          {t('documentCurrency.quote', {
            currency: foreign,
            rate: rate(quote.data.rate),
            base: base.code,
            date: formatDate(new Date(`${quote.data.rate_date}T00:00:00`), locale),
          })}
        </p>
      ) : null}
      {foreign && rateDate && quote.isError ? (
        <p className="text-danger text-small" role="alert" data-testid="document-currency-no-rate">
          {t('documentCurrency.noRate', { currency: foreign })}
        </p>
      ) : null}
    </div>
  )
}
